import { useRef, useState } from 'react';
import { Building2, ShieldCheck, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PaneHeader } from '@/components/settings/pane-header';
import { PreferenceRow, Switch } from '@/components/settings/settings-controls';
import { SaveBar } from '@/components/settings/queues/save-bar';
import { CalendarKindChip, PlatoOriginBadge } from '@/components/settings/calendars/calendar-badges';
import { EntitySelect } from '@/components/settings/calendars/entity-select';
import { TimezoneSelect } from '@/components/settings/calendars/timezone-select';
import { createAdminCalendar, updateAdminCalendar } from '@/lib/api/admin-calendars';
import {
  CALENDAR_NAME_MAX_LENGTH,
  buildCalendarUpdateBody,
  calendarDraftsEqual,
  calendarKind,
  calendarKindLabel,
  isPlatoCalendar,
  lockedFields,
  lookupOptionLabel,
  makeCalendarDraft,
  normalizeCalendar,
  normalizeCalendarDraft,
  usageCounts,
  usageSummary,
  validateCalendarDraft,
} from '@/lib/calendars';
import { appTimeZone } from '@/lib/timezone';
import { cn } from '@/lib/utils';

function Field({ label, locked = false, error, hint, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-[11.5px] font-bold text-muted-foreground">
        <span>{label}</span>
        {locked ? <PlatoOriginBadge className="text-[10px]" /> : null}
      </div>
      {children}
      {error ? (
        <p className="mt-1 text-[12px] font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function Card({ title, children, className }) {
  return (
    <div className={cn('space-y-3 rounded-xl bg-card px-4 py-3.5 shadow-[inset_0_0_0_1px_var(--border)]', className)}>
      {title ? (
        <span className="block text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">{title}</span>
      ) : null}
      {children}
    </div>
  );
}

/**
 * One calendar's editor in `mode` "create" (POST on Create) or "edit" (PUT on Save). The draft
 * is the 7-field body shape (normalizeCalendarDraft); `savedRef` holds the last server document
 * so dirty = deep compare and Discard is a reset. Plato-origin calendars lock every field but
 * the blocking rule (lockedFields) and PUT the SAVED document plus the new rule
 * (buildCalendarUpdateBody) so the server's 409 never trips on an echo.
 */
export function CalendarEditorPane({
  mode = 'edit',
  initialCalendar = null,
  initialDraft = null,
  canEdit = true,
  doctorOptions = [],
  clinicOptions = [],
  onBack,
  onSaved,
}) {
  const isCreate = mode === 'create';
  const savedRef = useRef(isCreate ? null : normalizeCalendar(initialCalendar));
  const baselineRef = useRef(
    isCreate ? normalizeCalendarDraft(initialDraft || makeCalendarDraft({ timezone: appTimeZone() })) : normalizeCalendarDraft(savedRef.current),
  );
  const [draft, setDraft] = useState(() => ({ ...baselineRef.current }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const saved = savedRef.current;
  const plato = !isCreate && isPlatoCalendar(saved);
  const locked = lockedFields(saved);
  const isLocked = (field) => !canEdit || locked.has(field);
  const dirty = !calendarDraftsEqual(draft, baselineRef.current);

  const edit = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    if (errors && Object.keys(errors).length > 0) {
      const next = { ...errors };
      for (const key of Object.keys(patch)) delete next[key];
      setErrors(next);
    }
  };

  const kind = calendarKind(draft);
  const standalone = kind === 'standalone';
  const doctorName = lookupOptionLabel(doctorOptions, draft.doctorId);
  const clinicName = lookupOptionLabel(clinicOptions, draft.tenantId);
  const usage = usageCounts(saved?.usage);
  const ownsSchedule = usage.slots > 0 || usage.sessionBlocks > 0;

  const handleSave = async () => {
    if (saving || !canEdit) return;
    const validation = validateCalendarDraft(draft);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      toast.error(Object.values(validation)[0]);
      return;
    }
    setSaving(true);
    try {
      const response = isCreate
        ? await createAdminCalendar({ body: normalizeCalendarDraft(draft) })
        : await updateAdminCalendar({ calendarId: saved.id, body: buildCalendarUpdateBody(saved, draft) });
      const next = normalizeCalendar(response);
      if (!next.id) throw new Error('The server did not return the saved calendar.');
      savedRef.current = next;
      baselineRef.current = normalizeCalendarDraft(next);
      setDraft({ ...baselineRef.current });
      setErrors({});
      onSaved?.(next);
      toast.success('Calendar saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save calendar');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft({ ...baselineRef.current });
    setErrors({});
  };

  const crumbName = draft.name.trim() || (isCreate ? 'New calendar' : saved?.name || 'Untitled calendar');

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        crumb={[{ label: 'Calendars', onClick: onBack }, { label: crumbName }]}
        title={isCreate ? 'New calendar' : 'Edit calendar'}
        action={
          <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-foreground">
            Active
            <Switch
              checked={draft.status === 'active'}
              onChange={(v) => edit({ status: v ? 'active' : 'inactive' })}
              label="Calendar active"
              disabled={isLocked('status')}
            />
          </label>
        }
      />

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex max-w-4xl flex-col gap-5 p-6">
          {plato ? (
            <div className="flex items-start gap-2.5 rounded-lg bg-cyan-50 px-3.5 py-2.5 text-[12.5px] text-cyan-900 ring-1 ring-inset ring-cyan-300/70 dark:bg-cyan-950/50 dark:text-cyan-100 dark:ring-cyan-500/40">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                <span className="font-bold">Synced from Plato</span> — only the blocking rule can be changed here. Name,
                owner, timezone, status and local-only are managed by the Plato sync.
              </span>
            </div>
          ) : null}
          {!canEdit ? (
            <p className="text-[12.5px] text-muted-foreground">
              View-only: your access level doesn’t allow editing calendars.
            </p>
          ) : null}

          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex-1 space-y-4">
              <Field label="Calendar name" locked={locked.has('name')} error={errors.name}>
                <Input
                  value={draft.name}
                  onChange={(e) => edit({ name: e.target.value })}
                  maxLength={CALENDAR_NAME_MAX_LENGTH}
                  disabled={isLocked('name')}
                  aria-invalid={errors.name ? true : undefined}
                  className={cn('h-9 text-sm font-semibold', errors.name ? 'ring-1 ring-destructive' : '')}
                  placeholder="e.g. Laser room, Dr Tan — Saturdays"
                />
              </Field>

              <Card title="Owner">
                {/* One field per row: the Owner card sits in the narrow half of the editor grid,
                    and a two-column split truncated "No doctor" / "No clinic" to a single letter. */}
                <div className="grid gap-3">
                  <Field
                    label="Doctor"
                    locked={locked.has('doctorId')}
                    hint={
                      !isCreate && !plato && ownsSchedule
                        ? `Owns ${usageSummary(usage).split(' · ').slice(0, 2).join(' and ')} — delete them before changing the doctor.`
                        : undefined
                    }
                  >
                    <EntitySelect
                      value={draft.doctorId}
                      onChange={(id) => edit({ doctorId: id })}
                      options={doctorOptions}
                      noneLabel="No doctor"
                      icon={Stethoscope}
                      disabled={isLocked('doctorId')}
                      ariaLabel="Doctor"
                      searchPlaceholder="Search doctors…"
                      emptyText="No doctors found."
                    />
                  </Field>
                  <Field label="Clinic" locked={locked.has('tenantId')}>
                    <EntitySelect
                      value={draft.tenantId}
                      onChange={(id) => edit({ tenantId: id })}
                      options={clinicOptions}
                      noneLabel="No clinic"
                      icon={Building2}
                      disabled={isLocked('tenantId')}
                      ariaLabel="Clinic"
                      searchPlaceholder="Search clinics…"
                      emptyText="No clinics found."
                    />
                  </Field>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <CalendarKindChip kind={kind} label={calendarKindLabel(draft, { doctorName, clinicName })} />
                  <span className="text-[11.5px] leading-relaxed text-muted-foreground">
                    No doctor and no clinic = standalone calendar (entries are added directly in the Calendar).
                  </span>
                </div>
              </Card>

              <Field label="Timezone" locked={locked.has('timezone')} error={errors.timezone}>
                <TimezoneSelect
                  value={draft.timezone}
                  onChange={(zone) => edit({ timezone: zone })}
                  disabled={isLocked('timezone')}
                  invalid={Boolean(errors.timezone)}
                />
              </Field>
            </div>

            <div className="w-full space-y-4 md:w-80">
              <Card title="Behaviour">
                <PreferenceRow
                  title="Non-blocking"
                  description={
                    standalone
                      ? 'Standalone calendars are always independent.'
                      : "Independent of the doctor's other calendars — its appointments never block the doctor elsewhere."
                  }
                  checked={standalone ? true : Boolean(draft.nonBlocking)}
                  onChange={(v) => edit({ nonBlocking: v })}
                  disabled={isLocked('nonBlocking') || standalone}
                />
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-border" />
                  {locked.has('localOnly') ? <PlatoOriginBadge className="text-[10px]" /> : null}
                </div>
                <PreferenceRow
                  title="Local only"
                  description="Never pushed to the Plato EMR."
                  checked={Boolean(draft.localOnly)}
                  onChange={(v) => edit({ localOnly: v })}
                  disabled={isLocked('localOnly')}
                />
              </Card>

              {!isCreate ? (
                <Card title="Usage">
                  <p className="text-[13px] font-semibold text-foreground">{usageSummary(usage)}</p>
                  <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                    {plato
                      ? 'Plato calendars cannot be deleted here.'
                      : 'Deleting a calendar (from the Calendars list) removes its slots and session blocks; regular appointments are kept.'}
                  </p>
                </Card>
              ) : (
                <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                  Blocking (default) calendars join the doctor's shared busy pool. A calendar with no doctor and no
                  clinic is standalone: always isolated, filled by direct entries in the Calendar page.
                </p>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>

      {canEdit ? (
        <SaveBar
          dirty={dirty}
          saving={saving}
          canSave={canEdit}
          onSave={handleSave}
          onDiscard={handleDiscard}
          saveLabel={isCreate ? 'Create calendar' : 'Save for everyone'}
          leftActions={
            plato ? (
              <span className="text-[12px] text-muted-foreground">Only the blocking rule is sent for a Plato calendar.</span>
            ) : null
          }
        />
      ) : (
        <div className="flex shrink-0 items-center border-t border-border bg-background px-6 py-3 text-[12px] text-muted-foreground">
          Read-only view.
        </div>
      )}
    </div>
  );
}
